import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { SatelliteHole } from '@/components/hole/SatelliteHole';
import { SchematicHole } from '@/components/hole/SchematicHole';
import { ButtonLink } from '@/components/ui/Button';
import { EmptyState, Skeleton } from '@/components/ui/Feedback';
import { ScoreMark } from '@/components/ui/ScoreMark';
import { useToast } from '@/components/ui/Toast';
import { completedRounds, useAllRounds, useCourse, useRoundDetails } from '@/lib/api/queries';
import type { CourseDetail } from '@/lib/api/types';
import { cx } from '@/lib/cx';
import { fmtDateShort } from '@/lib/format/date';
import { fmtDecimal, fmtPct } from '@/lib/format/number';
import { haversineMeters, type GeoPoint } from '@/lib/geo/geoPoint';
import { parseHole } from '@/lib/geo/holeGeometry';
import type { ImagerySource } from '@/lib/geo/imagery';
import { loadUnit, toUnit, unitLabel } from '@/lib/geo/units';
import { t, tx } from '@/lib/i18n';
import {
  MIN_ROUNDS_FOR_HOLE_INSIGHT,
  difficultyClause,
  holeStatsFor,
  type HoleStats,
} from '@/lib/stats/holeStats';
import { teeColor } from '@/routes/record/teeColor';
import { routes } from '@/routes/paths';
import { HolePicker, TeePicker } from './Pickers';
import styles from './HoleViewer.module.css';
import { CloseIcon } from '@/components/icons';
import { RulerIcon } from '@/components/icons';
import { ShareIcon } from '@/components/icons';
import { MapModeIcon } from '@/components/icons';

type Mode = 'schematic' | 'satellite';

export function HoleViewer() {
  const { courseId, holeNumber } = useParams<{ courseId: string; holeNumber: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const toast = useToast();

  const course = useCourse(courseId);
  const allRounds = useAllRounds();
  const unit = loadUnit();

  const [mode, setMode] = useState<Mode>('satellite');
  const [tilesFailed, setTilesFailed] = useState(false);
  const [rulerOn, setRulerOn] = useState(false);
  const [target, setTarget] = useState<GeoPoint | null>(null);
  const [imagery, setImagery] = useState<{
    source: ImagerySource;
    fallback: string | null;
  } | null>(null);
  const [stage, setStage] = useState({ width: 0, height: 0 });

  const current = Number(holeNumber) || 1;
  const teeParam = params.get('tee');

  /**
   * Callback ref, not `useRef` + `useEffect([])`: the stage does not exist on first render
   * (the component is showing a skeleton while the course loads), so a mount-time effect
   * would find a null ref, give up, and never run again — leaving the viewer blank.
   */
  const observerRef = useRef<ResizeObserver | null>(null);
  const stageRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;

    const measure = (width: number, height: number) =>
      setStage((prev) =>
        prev.width === width && prev.height === height ? prev : { width, height },
      );

    const rect = el.getBoundingClientRect();
    measure(Math.round(rect.width), Math.round(rect.height));

    const ro = new ResizeObserver(([entry]) => {
      if (!entry) return;
      measure(Math.round(entry.contentRect.width), Math.round(entry.contentRect.height));
    });
    ro.observe(el);
    observerRef.current = ro;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const data = course.data;
  const hole = data?.holes.find((h) => h.number === current);
  const teeName = teeParam ?? data?.teeBoxes[0]?.name ?? null;
  const shapes = useMemo(() => (hole ? parseHole(hole) : null), [hole]);

  // History at this course, for the insight card and "your result".
  const roundsHere = useMemo(
    () => completedRounds(allRounds.data?.rounds ?? []).filter((r) => r.courseId === courseId),
    [allRounds.data, courseId],
  );
  const details = useRoundDetails(roundsHere.slice(0, 20).map((r) => r.id));

  const stats = useMemo(
    () => (data ? holeStatsFor(data, details.rounds) : new Map<number, HoleStats>()),
    [data, details.rounds],
  );
  const holeStats = stats.get(current) ?? null;

  /** The most recent score on this hole, for the "Seu resultado aqui" card. */
  const lastResult = useMemo(() => {
    if (!data) return null;
    for (const round of details.rounds) {
      const score = round.scores.find(
        (s) => ((s.holeNumber - 1) % data.totalHoles) + 1 === current,
      );
      if (score) return { score, playedAt: round.completedAt ?? round.startedAt };
    }
    return null;
  }, [details.rounds, data, current]);

  const effectiveMode: Mode = tilesFailed ? 'schematic' : mode;

  // Changing hole must not carry a stale measurement over.
  useEffect(() => {
    setTarget(null);
  }, [current]);

  if (course.isLoading) return <ViewerSkeleton />;

  if (!data || !hole || !shapes) {
    return (
      <EmptyState
        title={t('hole.notFound.title')}
        actions={
          <ButtonLink
            to={courseId ? routes.course(courseId) : routes.courses}
            variant="secondary"
            block
          >
            {t('hole.notFound.action')}
          </ButtonLink>
        }
      >
        {t('hole.notFound.body', { number: current })}
      </EmptyState>
    );
  }

  const distanceMeters = teeName ? hole.distancesByTee[teeName] : undefined;
  const distanceValue = distanceMeters != null ? toUnit(distanceMeters, unit) : null;
  const suffix = unitLabel(unit);
  const distanceLabel = distanceValue != null ? `${distanceValue} ${suffix}` : '—';

  const teeGeo =
    shapes.tees.find((t) => t.name === teeName)?.point ?? shapes.tees[0]?.point ?? null;
  const rulerMeters = teeGeo && target ? haversineMeters(teeGeo, target) : null;

  const setTee = (name: string) => {
    const next = new URLSearchParams(params);
    next.set('tee', name);
    setParams(next, { replace: true });
  };

  const goToHole = (n: number) =>
    navigate(`${routes.hole(data.id, n)}${teeName ? `?tee=${encodeURIComponent(teeName)}` : ''}`, {
      replace: true,
    });

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t('hole.linkCopied'));
    } catch {
      toast.error(t('hole.copyFailed'));
    }
  };

  const stageContent =
    stage.width > 0 && stage.height > 0 ? (
      effectiveMode === 'satellite' ? (
        <SatelliteHole
          shapes={shapes}
          teeName={teeName}
          width={stage.width}
          height={stage.height}
          label={holeLabel(hole.number, hole.par, hole.handicapIndex, distanceLabel)}
          target={target}
          onPickTarget={rulerOn ? setTarget : undefined}
          onImageFailed={() => setTilesFailed(true)}
          onSourceResolved={(source, fallback) => setImagery({ source, fallback })}
        />
      ) : (
        <SchematicHole
          shapes={shapes}
          teeName={teeName}
          width={stage.width}
          height={stage.height}
          label={holeLabel(hole.number, hole.par, hole.handicapIndex, distanceLabel)}
        />
      )
    ) : null;

  const modeToggle = (
    <div className={styles.modeToggle} role="group" aria-label={t('hole.modeAria')}>
      <button
        type="button"
        className={cx(styles.modeOption, effectiveMode === 'schematic' && styles.modeActive)}
        onClick={() => {
          setTilesFailed(false);
          setMode('schematic');
        }}
      >
        {t('hole.schematic')}
      </button>
      <button
        type="button"
        className={cx(styles.modeOption, effectiveMode === 'satellite' && styles.modeActive)}
        onClick={() => {
          setTilesFailed(false);
          setMode('satellite');
        }}
      >
        {t('hole.satellite')}
      </button>
    </div>
  );

  return (
    <div className={styles.page}>
      {/* ------------------------------------------------------ desktop bar */}
      <header className={styles.deskBar}>
        <div className={styles.deskLeft}>
          <button
            type="button"
            className={styles.deskClose}
            onClick={() => navigate(-1)}
            aria-label={t('hole.closeViewer')}
          >
            <CloseIcon size={18} />
          </button>
          <div>
            <div className={styles.deskCourse}>{data.name}</div>
            <div className={styles.deskCity}>{data.city ?? ''}</div>
          </div>
        </div>

        <div className={styles.deskActions}>
          {modeToggle}
          <button
            type="button"
            className={cx(styles.deskPill, rulerOn && styles.deskPillAccent)}
            onClick={() => {
              setRulerOn((on) => !on);
              if (rulerOn) setTarget(null);
            }}
            aria-pressed={rulerOn}
            disabled={effectiveMode !== 'satellite'}
          >
            {rulerOn ? t('hole.rulerOn') : t('hole.ruler')}
          </button>
          <button type="button" className={styles.deskPill} onClick={() => void share()}>
            {t('round.share')}
          </button>
        </div>
      </header>

      <div className={styles.main}>
        {/* ------------------------------------------------- mobile chrome */}
        <div className={styles.topBar}>
          <button
            type="button"
            className={cx(styles.iconButton, styles.iconButtonPrimary)}
            onClick={() => navigate(-1)}
            aria-label={t('hole.closeViewer')}
          >
            <CloseIcon size={18} />
          </button>
          <div className={styles.topActions}>
            <button
              type="button"
              className={cx(styles.iconButton, rulerOn && styles.iconButtonActive)}
              onClick={() => {
                setRulerOn((on) => !on);
                if (rulerOn) setTarget(null);
              }}
              aria-pressed={rulerOn}
              aria-label={t('hole.ruler')}
              disabled={effectiveMode !== 'satellite'}
            >
              <RulerIcon size={18} />
            </button>
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => void share()}
              aria-label={t('hole.shareHole')}
            >
              <ShareIcon size={18} />
            </button>
            <button
              type="button"
              className={cx(styles.iconButton, styles.iconButtonInverted)}
              onClick={() => {
                setTilesFailed(false);
                setMode((m) => (m === 'satellite' ? 'schematic' : 'satellite'));
              }}
              aria-label={
                effectiveMode === 'satellite' ? t('hole.toSchematic') : t('hole.toSatellite')
              }
            >
              <MapModeIcon size={18} />
            </button>
          </div>
        </div>

        <div className={styles.pickers}>
          <HolePicker holes={data.holes.map((h) => h.number)} value={current} onChange={goToHole} />
          <TeePicker teeBoxes={data.teeBoxes} value={teeName} onChange={setTee} />
        </div>

        <div className={styles.stage} ref={stageRef}>
          <div className={styles.stageInner}>{stageContent}</div>

          {distanceValue != null && (
            <span className={styles.distanceChip}>
              {distanceValue} {suffix}
            </span>
          )}

          {rulerMeters != null && (
            <div className={styles.rulerChip}>
              <span className={styles.rulerValue}>
                {t('hole.rulerValue', { value: toUnit(rulerMeters, unit), unit: suffix })}
              </span>
              <span className={styles.rulerHint}>{t('hole.rulerHint')}</span>
            </div>
          )}

          {rulerOn && !target && effectiveMode === 'satellite' && (
            <p className={styles.rulerPrompt}>{t('hole.rulerPrompt')}</p>
          )}

          <div className={styles.mobileToggle}>{modeToggle}</div>
        </div>

        {/*
          Nada de texto sobre o mapa além do traçado, da distância e dos seletores (canvas 1f/1g).
          A atribuição da imagem é requisito legal do provedor, então ela não pode simplesmente
          sumir — desce para o chrome, em texto discreto e fora da foto, onde continua legível
          sem competir com o buraco.
        */}
        <p className={styles.imageryCredit}>
          {effectiveMode === 'satellite'
            ? (imagery?.source.attribution ?? t('hole.imageryGeneric'))
            : t('hole.imagerySchematic')}
        </p>

        {/* -------------------------------------------------- mobile body */}
        <div className={styles.mobileBody}>
          {imagery?.fallback && (
            <p className={styles.providerNote}>
              {t('hole.imageryFallback', { reason: imagery.fallback })}
            </p>
          )}

          {lastResult && effectiveMode === 'satellite' && (
            <YourResult
              strokes={lastResult.score.strokes}
              par={lastResult.score.par}
              putts={lastResult.score.putts}
              fairwayHit={lastResult.score.fairwayHit}
              playedAt={lastResult.playedAt}
            />
          )}

          <HoleInsight stats={holeStats} loading={details.isLoading} />

          <div className={styles.readout}>
            <div className={styles.facts}>
              <div>
                <div className={styles.factLabel}>{t('scorecard.par')}</div>
                <div className={styles.factValue}>{hole.par}</div>
              </div>
              <div>
                <div className={styles.factLabel}>{t('scorecard.si')}</div>
                <div className={styles.factValue}>{hole.handicapIndex}</div>
              </div>
            </div>
            <div className={styles.distance}>
              <div className={styles.factLabel}>{t('hole.teeToPin')}</div>
              <div className={styles.distanceValue}>
                {distanceValue ?? '—'} <span className={styles.distanceUnit}>{suffix}</span>
              </div>
            </div>
          </div>

          <div className={styles.strip}>
            <div className={styles.stripRule} aria-hidden="true" />
            <div className={cx(styles.stripScroll, 'scroll-x')}>
              {data.holes.map((h) => (
                <button
                  key={h.number}
                  type="button"
                  className={cx(styles.stripItem, h.number === current && styles.stripCurrent)}
                  onClick={() => goToHole(h.number)}
                  aria-current={h.number === current ? 'true' : undefined}
                  aria-label={t('hole.number', { number: h.number })}
                >
                  {h.number}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* -------------------------------------------------- desktop panel */}
      <aside className={styles.panel}>
        <div className={styles.panelHead}>
          <TeePicker teeBoxes={data.teeBoxes} value={teeName} onChange={setTee} compact />
          {activeRating(data, teeName)}
        </div>

        <div className={styles.panelDistance}>
          <div className={styles.panelLabel}>{t('hole.teeToPin')}</div>
          <div className={styles.panelDistanceValue}>
            {distanceValue ?? '—'} <span className={styles.panelDistanceUnit}>{suffix}</span>
          </div>
        </div>

        <div className={styles.panelFacts}>
          <div className={styles.panelFact}>
            <div className={styles.factLabel}>{t('scorecard.par')}</div>
            <div className={styles.panelFactValue}>{hole.par}</div>
          </div>
          <div className={styles.panelFact}>
            <div className={styles.factLabel}>{t('hole.strokeIndex')}</div>
            <div className={styles.panelFactValue}>{hole.handicapIndex}</div>
          </div>
          <div className={styles.panelFact}>
            <div className={styles.factLabel}>{t('hole.yourScore')}</div>
            <div className={styles.panelFactValue}>
              {lastResult ? lastResult.score.strokes : '—'}
              {lastResult && (
                <span className={styles.panelFactMeta}> {fmtDateShort(lastResult.playedAt)}</span>
              )}
            </div>
          </div>
        </div>

        <div className={styles.panelSection}>
          <div className={styles.panelLabel}>{t('hole.distanceByTee')}</div>
          {data.teeBoxes.map((t) => (
            <button
              key={t.name}
              type="button"
              className={cx(styles.teeRow, t.name === teeName && styles.teeRowActive)}
              onClick={() => setTee(t.name)}
            >
              <span className={styles.teeRowName}>
                <span
                  className={styles.teeDot}
                  style={{ background: teeColor(t) }}
                  aria-hidden="true"
                />
                {t.name}
              </span>
              <span>
                {hole.distancesByTee[t.name] != null
                  ? `${toUnit(hole.distancesByTee[t.name] as number, unit)} ${suffix}`
                  : '—'}
              </span>
            </button>
          ))}
        </div>

        <div className={styles.panelSection}>
          <HoleInsight stats={holeStats} loading={details.isLoading} tone="panel" />
        </div>

        <div className={styles.panelSection}>
          <div className={styles.panelLabel}>{t('round.holes')}</div>
          <div className={styles.panelStrip}>
            {data.holes.map((h) => (
              <button
                key={h.number}
                type="button"
                className={cx(styles.stripItem, h.number === current && styles.stripCurrent)}
                onClick={() => goToHole(h.number)}
                aria-current={h.number === current ? 'true' : undefined}
              >
                {h.number}
              </button>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}

function activeRating(course: CourseDetail, teeName: string | null) {
  const tee = course.teeBoxes.find((t) => t.name === teeName);
  if (!tee) return null;
  return (
    <span className={styles.panelRating}>
      {t('hole.rating', { cr: fmtDecimal(tee.courseRating), slope: tee.slopeRating })}
    </span>
  );
}

/**
 * The insight card (canvas 1f/1g). Says nothing below three rounds: an average built from
 * one or two is a coincidence, and the canvas would rather be silent than confident.
 */
function HoleInsight({
  stats,
  loading,
  tone = 'ink',
}: {
  stats: HoleStats | null;
  loading: boolean;
  tone?: 'ink' | 'panel';
}) {
  if (loading) {
    return (
      <div className={cx(styles.insight, tone === 'panel' && styles.insightPanel)}>
        <Skeleton height={12} width="70%" />
        <Skeleton height={9} width="40%" style={{ marginTop: 8 }} />
      </div>
    );
  }

  if (!stats || stats.rounds < MIN_ROUNDS_FOR_HOLE_INSIGHT) {
    return (
      <div className={cx(styles.insight, tone === 'panel' && styles.insightPanel)}>
        <span className={styles.insightIcon} aria-hidden="true">
          i
        </span>
        <div className={styles.insightBody}>
          <p className={styles.insightText}>{t('hole.insight.tooFew')}</p>
          <p className={styles.insightSample}>
            {t('hole.insight.sample', {
              have: stats?.rounds ?? 0,
              need: MIN_ROUNDS_FOR_HOLE_INSIGHT,
            })}
          </p>
        </div>
      </div>
    );
  }

  const clause = difficultyClause(stats);
  const signed =
    stats.averageOverPar >= 0
      ? `+${fmtDecimal(stats.averageOverPar)}`
      : fmtDecimal(stats.averageOverPar);

  return (
    <div className={cx(styles.insight, tone === 'panel' && styles.insightPanel)}>
      <span className={styles.insightIcon} aria-hidden="true">
        i
      </span>
      <div className={styles.insightBody}>
        <p className={styles.insightText}>
          {tx('hole.insight.average', {
            average: <strong>{signed}</strong>,
            clause: clause ? ` — ${clause}` : '',
          })}
        </p>
        <p className={styles.insightSample}>
          {t('hole.insight.detail', {
            count: stats.rounds,
            best: stats.best,
            worst: stats.worst,
          })}
          {stats.fairwayPct != null
            ? t('hole.insight.fairway', { pct: fmtPct(stats.fairwayPct) })
            : ''}
        </p>
      </div>
    </div>
  );
}

function YourResult({
  strokes,
  par,
  putts,
  fairwayHit,
  playedAt,
}: {
  strokes: number;
  par: number;
  putts: number | null;
  fairwayHit: boolean | null;
  playedAt: string;
}) {
  const parts = [
    t('hole.strokes', { count: strokes }),
    putts != null ? t('hole.putts', { count: putts }) : null,
    fairwayHit === null ? null : fairwayHit ? t('hole.fairwayHit') : t('hole.fairwayMissed'),
  ].filter(Boolean);

  return (
    <div className={styles.yourResult}>
      <div className={styles.yourResultBody}>
        <div className={styles.yourResultLabel}>{t('hole.yourResultHere')}</div>
        <div className={styles.yourResultMeta}>
          {fmtDateShort(playedAt)} · {parts.join(', ')}
        </div>
      </div>
      <ScoreMark strokes={strokes} par={par} size="lg" onInk />
    </div>
  );
}

function ViewerSkeleton() {
  return (
    <div className={styles.page}>
      <div className={styles.main}>
        <div style={{ padding: '2rem 1rem' }}>
          <Skeleton height={28} width="50%" style={{ opacity: 0.25 }} />
          <Skeleton height={360} style={{ marginTop: 20, opacity: 0.2 }} radius={12} />
        </div>
      </div>
    </div>
  );
}

function holeLabel(number: number, par: number, si: number, distance: string): string {
  return t('hole.canvasLabel', { number, par, si, distance });
}
